import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import HeaderSection from '../content/HeaderSection';
import FooterSection from '../content/FooterSection';
import useGoogleAnalytics from '../hooks/useGoogleAnalytics';
import { usePageLanguage } from '../context/PageLanguageContext';
import SEOHead from '../components/SEOHead';

// Pricing. Three columns, one comparison table, and the two conditions that belong ABOVE the fold of
// a buying decision rather than in the small print: the software is sold as it is, and there is no
// support. A club that learns this after paying has been treated badly.

// One place for the version, so the download links and the label cannot drift apart.
const APP_VERSION = '1.2.3';

const FREE = 'free';
const WEB = 'web';
const DESK = 'desktop';

const translations = {
  de: {
    pageTitle: 'Preise – Rink Hockey Scoreboard',
    pageDescription:
      'Preise der drei Editionen: Web kostenlos, Web mit Entertainment CHF 150 pro Jahr, Desktop CHF 250 pro Jahr mit 30 Tagen Testphase.',
    backToHome: 'Zurück zur Startseite',
    tag: 'Preise',
    title: 'Drei Editionen, ein Preis pro Jahr',
    lead: 'Keine Staffelung nach Mannschaften, keine Gebühr pro Spiel. Ein Verein, ein Preis.',
    perYear: 'pro Jahr',
    free: 'Kostenlos',
    freeNote: 'dauerhaft',
    trial: '30 Tage testen',
    plans: {
      [FREE]: { name: 'Web', pitch: 'Das vollständige Scoreboard im Browser.', cta: 'Jetzt starten', to: '/control' },
      [WEB]: { name: 'Web mit Entertainment', pitch: 'Dazu Musik und Videos, von Hand gesteuert.', cta: 'Anfragen', to: null },
      [DESK]: { name: 'Desktop', pitch: 'Alles davon — und die Unterhaltung läuft von selbst.', cta: 'Testversion herunterladen', to: '#testversion', highlight: true },
    },
    groups: [
      {
        title: 'Scoreboard',
        rows: [
          { label: 'Spielzeit, Perioden, Pausen', free: true, web: true, desktop: true },
          { label: 'Strafen mit automatischer Teamstrafe', free: true, web: true, desktop: true },
          { label: 'Teamfouls und Timeouts', free: true, web: true, desktop: true },
          { label: 'Zweiter Bildschirm für die Halle', free: true, web: true, desktop: true },
          { label: 'Vereinsfarbe und Sponsorenlogos', free: true, web: true, desktop: true },
        ],
      },
      {
        title: 'Unterhaltung',
        rows: [
          { label: 'Musik im Spiel', free: false, web: 'Manuell', desktop: 'Vollautomatisch' },
          { label: 'Pre-Game- und Sponsorenvideos', free: false, web: 'Manuell', desktop: 'Nahtlos eingebunden' },
          { label: 'Pausenmusik', free: false, web: 'Manuell', desktop: 'Automatisch, inkl. Playlist' },
          { label: 'Torjubel für den Verein', free: false, web: false, desktop: true },
          { label: 'Torjubel pro Spielerin', free: false, web: false, desktop: true },
          { label: 'Shutout-Jubel für die Torhüterin', free: false, web: false, desktop: true },
        ],
      },
      {
        title: 'Vorbereitung',
        rows: [
          { label: 'Teams mit Logo und Kader hinterlegen', free: false, web: false, desktop: true },
          { label: 'Konfiguration exportieren und übertragen', free: false, web: false, desktop: true },
          { label: 'Offline in der Halle', free: false, web: false, desktop: true },
          { label: 'Installation nötig', free: 'Nein', web: 'Nein', desktop: 'Ja' },
        ],
      },
    ],
    downloadTitle: 'Testversion herunterladen',
    downloadLead: '30 Tage, alle Funktionen, kein Konto und keine Zahlungsdaten. Nach Ablauf kannst du den Lizenzschlüssel direkt in der App eingeben — deine Einstellungen bleiben erhalten.',
    downloadWin: 'Windows (64-Bit)',
    downloadMac: 'macOS (Apple Silicon)',
    downloadVersion: `Version ${APP_VERSION}`,
    downloadNotesTitle: 'Bitte vor dem Start lesen',
    downloadNotes: [
      'Die Dateien sind nicht signiert. Windows meldet beim ersten Start „Der Computer wurde durch Windows geschützt" — über „Weitere Informationen" und „Trotzdem ausführen" geht es weiter. macOS meldet beim ersten Start, Apple könne die App nicht überprüfen: Schliesse die Meldung, öffne „Systemeinstellungen → Datenschutz & Sicherheit" und klicke dort unten auf „Trotzdem öffnen".',
      'Die macOS-Version läuft nur auf Apple Silicon (M1 und neuer), nicht auf älteren Intel-Macs.',
      'Windows braucht die WebView2-Runtime. Auf Windows 11 ist sie vorhanden, auf Windows 10 in aller Regel auch; falls nicht, installiert das Setup sie nach.',
      'Es wird kein Support geleistet — die Anleitung im Handbuch ist der vorgesehene Weg.',
    ],
    termsTitle: 'Was im Preis enthalten ist — und was nicht',
    terms: [
      {
        t: 'Software wie sie ist',
        d: 'Du erhältst die Software in ihrem aktuellen Funktionsumfang. Es gibt keine Zusicherung bestimmter Funktionen und keinen Anspruch auf Weiterentwicklung.',
      },
      {
        t: 'Kein Support',
        d: 'Es wird kein Support geleistet. Handbuch und Videoanleitungen stehen frei zur Verfügung; darüber hinaus gibt es keine Hilfestellung.',
      },
      {
        t: 'Erst testen, dann zahlen',
        d: 'Die Desktop-Edition läuft 30 Tage mit vollem Funktionsumfang, ohne Konto und ohne Zahlungsdaten. Danach entscheidest du.',
      },
      {
        t: 'Die kostenlose Version bleibt kostenlos',
        d: 'Die Web-Version ohne Unterhaltung war immer kostenlos und bleibt es. Sie wird nicht beschnitten, um die bezahlten Editionen attraktiver zu machen.',
      },
    ],
    faqTitle: 'Häufige Fragen',
    faq: [
      { q: 'Brauche ich für die Desktop-Edition Internet?', a: 'Nur zum Aktivieren der Lizenz. In der Halle läuft sie offline — ausser du nutzt eine YouTube-Playlist für die Pausenmusik.' },
      { q: 'Auf wie vielen Rechnern darf ich sie installieren?', a: 'Die Lizenz gilt für deinen Verein. Für den Ersatzrechner in der Halle brauchst du keine zweite.' },
      { q: 'Was passiert nach den 30 Tagen?', a: 'Die Desktop-App lässt sich nicht mehr bedienen und verweist auf die kostenlose Web-Version. Deine Daten bleiben liegen; nach der Aktivierung ist alles wieder da.' },
      { q: 'Warum kostet die Web-Version mit Entertainment etwas?', a: 'Sie enthält die Medienfunktionen. Das Scoreboard selbst bleibt in der kostenlosen Version vollständig.' },
    ],
    contact: 'Interesse? Schreib uns — wir melden uns mit den Aktivierungsdaten.',
    contactCta: 'Kontakt aufnehmen',
  },
  en: {
    pageTitle: 'Pricing – Rink Hockey Scoreboard',
    pageDescription:
      'Pricing for the three editions: Web free, Web with Entertainment CHF 150 per year, Desktop CHF 250 per year with a 30-day trial.',
    backToHome: 'Back to home',
    tag: 'Pricing',
    title: 'Three editions, one price per year',
    lead: 'No tiering by number of teams, no fee per match. One club, one price.',
    perYear: 'per year',
    free: 'Free',
    freeNote: 'permanently',
    trial: '30-day trial',
    plans: {
      [FREE]: { name: 'Web', pitch: 'The complete scoreboard in the browser.', cta: 'Start now', to: '/control' },
      [WEB]: { name: 'Web with Entertainment', pitch: 'Plus music and video, driven by hand.', cta: 'Enquire', to: null },
      [DESK]: { name: 'Desktop', pitch: 'All of that — and the entertainment runs itself.', cta: 'Download the trial', to: '#testversion', highlight: true },
    },
    groups: [
      {
        title: 'Scoreboard',
        rows: [
          { label: 'Clock, periods, breaks', free: true, web: true, desktop: true },
          { label: 'Penalties with automatic team-penalty time', free: true, web: true, desktop: true },
          { label: 'Team fouls and timeouts', free: true, web: true, desktop: true },
          { label: 'Second screen for the venue', free: true, web: true, desktop: true },
          { label: 'Club colour and sponsor logos', free: true, web: true, desktop: true },
        ],
      },
      {
        title: 'Entertainment',
        rows: [
          { label: 'Music during the match', free: false, web: 'Manual', desktop: 'Fully automatic' },
          { label: 'Pre-game and sponsor videos', free: false, web: 'Manual', desktop: 'Woven in' },
          { label: 'Break music', free: false, web: 'Manual', desktop: 'Automatic, incl. playlist' },
          { label: 'Goal celebration for the club', free: false, web: false, desktop: true },
          { label: 'Goal celebration per player', free: false, web: false, desktop: true },
          { label: 'Shutout celebration for the goalkeeper', free: false, web: false, desktop: true },
        ],
      },
      {
        title: 'Preparation',
        rows: [
          { label: 'Teams with crest and roster', free: false, web: false, desktop: true },
          { label: 'Export the configuration and carry it over', free: false, web: false, desktop: true },
          { label: 'Offline in the hall', free: false, web: false, desktop: true },
          { label: 'Installation required', free: 'No', web: 'No', desktop: 'Yes' },
        ],
      },
    ],
    downloadTitle: 'Download the trial',
    downloadLead: '30 days, every feature, no account and no payment details. When it runs out you can enter the licence key straight into the app — your settings are kept.',
    downloadWin: 'Windows (64-bit)',
    downloadMac: 'macOS (Apple Silicon)',
    downloadVersion: `Version ${APP_VERSION}`,
    downloadNotesTitle: 'Please read before you start it',
    downloadNotes: [
      'The files are not signed. Windows says “Windows protected your PC” on first run — continue via “More info” and “Run anyway”. macOS says on first launch that Apple cannot verify the app: close the message, open “System Settings → Privacy & Security” and click “Open Anyway” near the bottom.',
      'The macOS build runs on Apple Silicon (M1 and newer) only, not on older Intel Macs.',
      'Windows needs the WebView2 runtime. Windows 11 has it, Windows 10 almost always does; if not, the installer fetches it.',
      'No support is provided — the manual is the intended route.',
    ],
    termsTitle: 'What the price includes — and what it does not',
    terms: [
      {
        t: 'Software as it is',
        d: 'You get the software with the features it has today. No particular feature is promised, and there is no entitlement to further development.',
      },
      {
        t: 'No support',
        d: 'No support is provided. The manual and the video guides are freely available; beyond those there is no assistance.',
      },
      {
        t: 'Try first, pay after',
        d: 'The desktop edition runs for 30 days with every feature, without an account and without payment details. Then you decide.',
      },
      {
        t: 'The free edition stays free',
        d: 'The web edition without entertainment has always been free and will stay that way. It is not cut back to make the paid editions look better.',
      },
    ],
    faqTitle: 'Common questions',
    faq: [
      { q: 'Does the desktop edition need internet?', a: 'Only to activate the licence. In the hall it runs offline — unless you use a YouTube playlist for the break music.' },
      { q: 'On how many machines may I install it?', a: 'The licence is for your club. You do not need a second one for the spare machine in the hall.' },
      { q: 'What happens after the 30 days?', a: 'The desktop app stops working and points to the free web edition. Your data stays where it is; after activation everything is back.' },
      { q: 'Why does the web edition with entertainment cost something?', a: 'It contains the media features. The scoreboard itself stays complete in the free edition.' },
    ],
    contact: 'Interested? Write to us and we will come back with the activation details.',
    contactCta: 'Get in touch',
  },
};

/** A table cell: ✓ / — / a short word when the answer is "yes, but differently". */
function Cell({ value }) {
  if (value === true) return <span className="text-lg font-bold text-blue-600" aria-label="enthalten">✓</span>;
  if (value === false) return <span className="text-stone-300" aria-label="nicht enthalten">—</span>;
  return <span className="text-sm font-medium text-stone-700">{value}</span>;
}

export default function Pricing() {
  useGoogleAnalytics();
  const { pageLanguage } = usePageLanguage();
  const t = translations[pageLanguage] || translations.de;
  const navigate = useNavigate();

  const price = {
    [FREE]: { amount: t.free, note: t.freeNote },
    [WEB]: { amount: 'CHF 150', note: t.perYear },
    [DESK]: { amount: 'CHF 250', note: `${t.perYear} · ${t.trial}` },
  };
  const columns = [FREE, WEB, DESK];

  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead
        title={t.pageTitle}
        description={t.pageDescription}
        lang={pageLanguage}
        canonical="https://www.rinkhockey-scoreboard.com/preise"
      />
      <HeaderSection />

      <main className="flex-grow bg-gray-50">
        <div className="mx-auto max-w-[1200px] px-8 py-16 max-md:px-5">
          <Link to="/" className="text-sm text-blue-600 hover:text-blue-800">
            ← {t.backToHome}
          </Link>

          <div className="mt-8 max-w-[760px]">
            <div className="text-sm font-semibold uppercase tracking-wider text-blue-600">{t.tag}</div>
            <h1 className="mt-3 text-5xl font-extrabold leading-tight text-stone-900 max-md:text-3xl">{t.title}</h1>
            <p className="mt-4 text-lg text-stone-600">{t.lead}</p>
          </div>

          {/* Plan cards */}
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {columns.map((key) => {
              const p = t.plans[key];
              return (
                <div
                  key={key}
                  className={`flex h-full flex-col rounded-2xl border bg-white p-8 ${
                    p.highlight ? 'border-blue-600 shadow-xl' : 'border-stone-200 shadow-sm'
                  }`}
                >
                  <div className="text-xl font-bold text-stone-900">{p.name}</div>
                  <p className="mt-2 text-base text-stone-600">{p.pitch}</p>
                  <div className="mt-6 flex items-baseline gap-2">
                    <span className="text-4xl font-extrabold text-stone-900">{price[key].amount}</span>
                  </div>
                  <div className="mt-1 mb-8 text-sm text-stone-500">{price[key].note}</div>
                  <button
                    type="button"
                    onClick={() => {
                      if (!p.to) return void (window.location.href = 'mailto:info@rinkhockey-scoreboard.com');
                      if (p.to.startsWith('#')) return void document.querySelector(p.to)?.scrollIntoView({ behavior: 'smooth' });
                      navigate(p.to);
                    }}
                    className={`mt-auto w-full rounded-lg px-5 py-3 text-sm font-semibold transition-colors ${
                      p.highlight
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'border border-stone-300 text-stone-800 hover:bg-stone-50'
                    }`}
                  >
                    {p.cta}
                  </button>
                </div>
              );
            })}
          </div>

          {/* The trial downloads. Deliberately next to the price rather than hidden in a footer: the
              caveats below are things a club must know BEFORE the file is on their machine. */}
          <section id="testversion" className="mt-16 scroll-mt-8 rounded-2xl border border-blue-200 bg-white p-8 md:p-10">
            <h2 className="text-2xl font-bold text-stone-900">{t.downloadTitle}</h2>
            <p className="mt-3 max-w-3xl text-base leading-relaxed text-stone-600">{t.downloadLead}</p>

            <div className="mt-6 flex flex-wrap items-center gap-4">
              <a
                href={`/download/rink-hockey-scoreboard-${APP_VERSION}-windows-x64-setup.exe`}
                className="rounded-lg bg-blue-600 px-6 py-4 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
              >
                ⬇ {t.downloadWin}
              </a>
              <a
                href={`/download/rink-hockey-scoreboard-${APP_VERSION}-macos-arm64.dmg`}
                className="rounded-lg border border-stone-300 px-6 py-4 text-sm font-semibold text-stone-800 transition-colors hover:bg-stone-50"
              >
                ⬇ {t.downloadMac}
              </a>
              <span className="text-sm text-stone-500">{t.downloadVersion}</span>
            </div>

            <div className="mt-8 rounded-xl border-l-4 border-amber-400 bg-amber-50 p-5">
              <div className="text-sm font-bold text-amber-900">{t.downloadNotesTitle}</div>
              <ul className="mt-3 space-y-2">
                {t.downloadNotes.map((n) => (
                  <li key={n} className="flex gap-2.5 text-sm leading-relaxed text-amber-900">
                    <span aria-hidden="true">•</span>
                    <span>{n}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Comparison table */}
          <div className="mt-16 overflow-x-auto rounded-2xl border border-stone-200 bg-white">
            <table className="w-full min-w-[720px] border-collapse text-left">
              <thead>
                <tr className="border-b border-stone-200">
                  <th scope="col" className="p-5 text-sm font-semibold text-stone-500" />
                  {columns.map((key) => (
                    <th key={key} scope="col" className="p-5 text-center text-sm font-bold text-stone-900">
                      {t.plans[key].name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {t.groups.map((g) => (
                  <React.Fragment key={g.title}>
                    <tr className="bg-stone-50">
                      <th
                        scope="colgroup"
                        colSpan={4}
                        className="p-4 text-sm font-semibold uppercase tracking-wider text-stone-500"
                      >
                        {g.title}
                      </th>
                    </tr>
                    {g.rows.map((row) => (
                      <tr key={row.label} className="border-t border-stone-100">
                        <th scope="row" className="p-4 text-base font-normal text-stone-700">
                          {row.label}
                        </th>
                        <td className="p-4 text-center">
                          <Cell value={row.free} />
                        </td>
                        <td className="p-4 text-center">
                          <Cell value={row.web} />
                        </td>
                        <td className="p-4 text-center">
                          <Cell value={row.desktop} />
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Terms — deliberately here, not in the footer. */}
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-stone-900">{t.termsTitle}</h2>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              {t.terms.map((item) => (
                <div key={item.t} className="rounded-xl border border-stone-200 bg-white p-6">
                  <div className="text-base font-bold text-stone-900">{item.t}</div>
                  <p className="mt-2 text-base leading-relaxed text-stone-600">{item.d}</p>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-stone-900">{t.faqTitle}</h2>
            <dl className="mt-6 space-y-4">
              {t.faq.map((item) => (
                <div key={item.q} className="rounded-xl border border-stone-200 bg-white p-6">
                  <dt className="text-base font-bold text-stone-900">{item.q}</dt>
                  <dd className="mt-2 text-base leading-relaxed text-stone-600">{item.a}</dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Contact */}
          <div className="mt-16 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 p-10 text-center text-white md:p-14">
            <p className="mx-auto max-w-2xl text-lg text-slate-200">{t.contact}</p>
            <a
              href="mailto:info@rinkhockey-scoreboard.com"
              className="mt-6 inline-block rounded-lg bg-blue-600 px-7 py-4 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
            >
              {t.contactCta}
            </a>
          </div>
        </div>
      </main>

      <FooterSection lang={pageLanguage} />
    </div>
  );
}
